/**
 * Funções auxiliares para exportação de canvas
 */

interface ElementStyles {
  element: HTMLElement;
  originalStyles: { [key: string]: string };
}

/**
 * Cria e injeta um estilo CSS temporário para exportação
 */
export const injectExportStyles = (): HTMLStyleElement => {
  const style = document.createElement('style');
  style.id = 'export-styles-temporary';
  style.textContent = `
    /* Remover truncamentos durante exportação - EXCETO losangos (decisões) */
    #canvas-content *:not([class*="rotate-45"]):not([class*="rotate"]):not([data-card-type="decisao"]):not([data-card-type="decisao"] *):not([data-decision-content]):not([data-decision-content] *),
    #canvas-content *:not([class*="rotate-45"]):not([class*="rotate"]):not([data-card-type="decisao"]):not([data-card-type="decisao"] *):not([data-decision-content]):not([data-decision-content] *)::before,
    #canvas-content *:not([class*="rotate-45"]):not([class*="rotate"]):not([data-card-type="decisao"]):not([data-card-type="decisao"] *):not([data-decision-content]):not([data-decision-content] *)::after {
      overflow: visible !important;
      overflow-x: visible !important;
      overflow-y: visible !important;
      white-space: normal !important;
      text-overflow: clip !important;
      max-height: none !important;
      max-width: none !important;
      -webkit-line-clamp: none !important;
      line-clamp: none !important;
      word-wrap: break-word !important;
      word-break: break-word !important;
    }
    
    /* Cards devem expandir para mostrar todo o conteúdo - EXCETO losangos/decisões */  
    #canvas-content [class*="absolute"]:not([class*="rotate-45"]):not([class*="rotate"]):not([data-card-type="decisao"]):not([data-card-type="decisao"] *):not([class*="translate"]):not([data-decision-content]):not([data-decision-content] *) {                                                                      
      height: auto !important;
      min-height: fit-content !important;
      max-height: none !important;
      overflow: visible !important;
      overflow-y: visible !important;
      overflow-x: visible !important;
    }
    
    /* Garantir que containers de cards não cortem conteúdo - EXCETO decisões */
    #canvas-content [class*="flex-col"]:not([class*="rotate-45"]):not([class*="rotate"]):not([data-card-type="decisao"]):not([data-card-type="decisao"] *):not([data-decision-content]):not([data-decision-content] *) {
      height: auto !important;
      min-height: fit-content !important;
      max-height: none !important;
      overflow: visible !important;
    }
    
    /* Proteger losangos (decisões) - NÃO alterar nada */
    #canvas-content [data-card-type="decisao"] {
      overflow: visible !important;
      transition: none !important;
      animation: none !important;
    }
    
    /* Proteger conteúdo de decisões - manter transform intacto */
    #canvas-content [data-decision-content="true"] {
      /* Preservar posicionamento original */
    }
    
    /* Manter transformações de centralização dentro de decisões */
    #canvas-content [data-card-type="decisao"] [class*="translate"] {
      /* Manter transform original - não sobrescrever */
    }
    
    /* Proteger losangos (decisões) - manter transformações intactas */
    #canvas-content [class*="rotate-45"] {
      transform: rotate(45deg) !important;
      height: 160px !important;
      width: 160px !important;
      overflow: visible !important;
    }
    
    /* Conteúdo interno do losango - manter counter-rotate */
    #canvas-content [class*="rotate-45"] > div,
    #canvas-content [class*="-rotate-45"] {
      transform: rotate(-45deg) !important;
    }
    
    /* Proteger cards no modo postit - manter altura fixa */
    #canvas-content [class*="postit"] {
      height: 160px !important;
      min-height: 160px !important;
      max-height: none !important;
    }
    
    /* Garantir que card inicial não tenha animações durante exportação */
    #canvas-content [class*="absolute"] {
      transition: none !important;
      animation: none !important;
    }
  `;
  document.head.appendChild(style);
  return style;
};

/**
 * Remove os estilos CSS temporários de exportação
 */
export const removeExportStyles = (styleElement: HTMLStyleElement | null): void => {
  if (styleElement && styleElement.parentNode) {
    styleElement.parentNode.removeChild(styleElement);
  }
};

/**
 * Prepara os elementos para exportação, removendo truncamentos e overflow
 */
export const prepareElementsForExport = (container: HTMLElement): ElementStyles[] => {
  const savedStyles: ElementStyles[] = [];
  const processedElements = new Set<HTMLElement>();
  
  // Encontrar TODOS os elementos que podem conter texto (cards, spans, divs, etc.)
  const allElements = container.querySelectorAll('*');
  
  allElements.forEach((el) => {
    const element = el as HTMLElement;
    
    // Pular elementos já processados
    if (processedElements.has(element)) return;
    
    // Verificar se tem classes de truncamento ou overflow
    const hasTruncateClass = element.classList.toString().includes('truncate') || 
                            element.classList.toString().includes('line-clamp') ||
                            element.classList.toString().includes('overflow-hidden');
    
    // Verificar estilos computed que causam truncamento
    const computedStyle = window.getComputedStyle(element);
    const hasOverflowHidden = computedStyle.overflow === 'hidden' || 
                             computedStyle.textOverflow === 'ellipsis' ||
                             computedStyle.webkitLineClamp !== 'none' &&
                             computedStyle.webkitLineClamp !== '';
    
    // PROTEGER LOSANGOS/DECISÕES - Não processar elementos com rotate-45 ou elementos dentro de losangos
    const isDiamond = element.classList.toString().includes('rotate-45') || 
                     element.classList.toString().includes('rotate') ||
                     element.getAttribute('data-card-type') === 'decisao' ||
                     element.getAttribute('data-decision-content') === 'true';
                     element.getAttribute('data-card-type') === 'decisao' ||
                     element.getAttribute('data-decision-content') === 'true';
    
    // Verificar se o elemento está dentro de um container de decisão/losango
    let parent = element.parentElement;
    let isInsideDiamond = false;
    while (parent && parent !== container) {
      if (parent.classList.toString().includes('rotate-45') || 
          parent.classList.toString().includes('rotate') ||
          parent.getAttribute('data-card-type') === 'decisao' ||
          parent.getAttribute('data-decision-content') === 'true' ||
          parent.querySelector('[class*="rotate-45"]') ||
          parent.querySelector('[data-card-type="decisao"]')) {
        isInsideDiamond = true;
        break;
      }
      parent = parent.parentElement;
    }
    
    if (isDiamond || isInsideDiamond) {
      return; // Pular losangos e seus filhos completamente
    }
    
    // Processar elementos com truncamento OU elementos de texto (spans, divs com texto)
    const isTextElement = ['SPAN', 'DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(element.tagName);
    const hasText = element.textContent && element.textContent.trim().length > 0;
    
    if (hasTruncateClass || hasOverflowHidden || (isTextElement && hasText)) {
      const originalStyles: { [key: string]: string } = {};
      
      // Salvar estilos originais importantes
      const styleProperties = [
        'overflow',
        'overflowX',
        'overflowY',
        'whiteSpace',
        'textOverflow',
        'maxHeight',
        'maxWidth',
        'height',
        'width',
        'lineClamp',
        'webkitLineClamp',
        'display',
      ];
      
      styleProperties.forEach((prop) => {
        const camelCaseProp = prop.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        originalStyles[prop] = element.style.getPropertyValue(prop) || 
                               element.style.getPropertyValue(camelCaseProp) || '';
      });
      
      // Remover classes que causam truncamento
      const classesToRemove = [
        'truncate', 
        'line-clamp-1', 
        'line-clamp-2', 
        'line-clamp-3', 
        'line-clamp-4',
        'overflow-hidden',
        'overflow-x-hidden',
        'overflow-y-hidden'
      ];
      
      classesToRemove.forEach((cls) => {
        if (element.classList.contains(cls)) {
          element.classList.remove(cls);
        }
      });
      
      // Aplicar estilos para permitir texto completo
      element.style.setProperty('overflow', 'visible', 'important');
      element.style.setProperty('overflow-x', 'visible', 'important');
      element.style.setProperty('overflow-y', 'visible', 'important');
      element.style.setProperty('white-space', 'normal', 'important');
      element.style.setProperty('text-overflow', 'clip', 'important');
      element.style.setProperty('max-height', 'none', 'important');
      element.style.setProperty('max-width', 'none', 'important');
      element.style.setProperty('-webkit-line-clamp', 'none', 'important');
      element.style.setProperty('line-clamp', 'none', 'important');
      element.style.setProperty('word-wrap', 'break-word', 'important');
      element.style.setProperty('word-break', 'break-word', 'important');
      
      // Se for um card container, remover altura fixa se houver
      // MAS NUNCA para losangos (já foram excluídos acima, mas garantia extra)                                                                               
      if (!isDiamond && (element.classList.toString().includes('flex-col') || 
          element.classList.toString().includes('h-full') ||
          element.classList.toString().includes('h-') ||
          element.classList.toString().includes('min-h-'))) {
        const computedHeight = computedStyle.height;
        const computedMinHeight = computedStyle.minHeight;
        
        // Salvar altura original se houver
        if (computedHeight && computedHeight !== 'auto' && computedHeight !== '0px') {
          originalStyles['fixedHeight'] = computedHeight;
        }
        
        // Permitir que o elemento expanda para mostrar todo o conteúdo
        element.style.setProperty('height', 'auto', 'important');
        element.style.setProperty('min-height', computedHeight || computedMinHeight || 'auto', 'important');
        element.style.setProperty('max-height', 'none', 'important');
      }
      
      // Garantir que elementos com overflow-hidden não cortem conteúdo
      if (computedStyle.overflow === 'hidden' || 
          computedStyle.overflowY === 'hidden' ||
          element.classList.toString().includes('overflow-hidden')) {
        element.style.setProperty('overflow', 'visible', 'important');
        element.style.setProperty('overflow-y', 'visible', 'important');
        element.style.setProperty('overflow-x', 'visible', 'important');
      }
      
      savedStyles.push({ element, originalStyles });
      processedElements.add(element);
    }
  });
  
  return savedStyles;
};

/**
 * Restaura os estilos originais após a exportação
 */
export const restoreElementsAfterExport = (savedStyles: ElementStyles[]): void => {
  savedStyles.forEach(({ element, originalStyles }) => {
    // Remover estilos importantes primeiro
    const propsToRemove = [
      'overflow', 'overflow-x', 'overflow-y', 'white-space', 'text-overflow',
      'max-height', 'max-width', 'height', 'width', '-webkit-line-clamp',
      'line-clamp', 'word-wrap', 'word-break'
    ];
    
    propsToRemove.forEach((prop) => {
      element.style.removeProperty(prop);
    });
    
    // Restaurar estilos originais
    Object.keys(originalStyles).forEach((prop) => {
      if (prop === 'fixedHeight') {
        // Restaurar altura fixa se havia
        if (originalStyles[prop]) {
          element.style.setProperty('height', originalStyles[prop]);
        }
      } else if (originalStyles[prop]) {
        element.style.setProperty(prop, originalStyles[prop]);
      }
    });
  });
};

/**
 * Obtém as dimensões completas do conteúdo (incluindo scroll)
 */
export const getFullContentDimensions = (element: HTMLElement): { width: number; height: number } => {                                                          
  console.log('[getFullContentDimensions] Iniciando cálculo de dimensões...');
  
  // Salvar estilos originais
  const originalOverflow = element.style.overflow;
  const originalPosition = element.style.position;
  const originalTransform = element.style.transform;

  // Forçar cálculo de dimensões sem alterar transform
  element.style.overflow = 'visible';
  element.style.position = 'relative';
  
  // Temporariamente remover transform para calcular dimensões reais
  const tempTransform = element.style.transform;
  element.style.transform = 'none';

  // Forçar layout recalculation
  element.offsetHeight; // Trigger reflow
  
  let maxRight = 0;
  let maxBottom = 0;
  let minLeft = Infinity;
  let minTop = Infinity;

  // Encontrar todos os elementos filhos (cards, setas SVG, post-its, etc.)
  const allElements = element.querySelectorAll('*');
  
  // Processar elementos normais (cards, post-its, etc.)
  allElements.forEach((child) => {
    const el = child as HTMLElement;
    
    // Pular elementos SVG internos (paths, markers, etc.) - processar apenas o SVG container
    if (el.tagName === 'path' || el.tagName === 'marker' || el.tagName === 'defs' || el.tagName === 'g') {
      return;
    }
    
    const computedStyle = window.getComputedStyle(el);
    const position = computedStyle.position;
    const display = computedStyle.display;

    // Pular elementos invisíveis
    if (display === 'none' || computedStyle.visibility === 'hidden') {
      return;
    }

    // Para SVG, usar getBBox se disponível
    if (el.tagName === 'svg') {
      try {
        const svg = el as SVGSVGElement;
        const bbox = svg.getBBox();
        const svgLeft = parseFloat(computedStyle.left) || 0;
        const svgTop = parseFloat(computedStyle.top) || 0;
        
        const right = svgLeft + bbox.width;
        const bottom = svgTop + bbox.height;
        
        minLeft = Math.min(minLeft, svgLeft);
        minTop = Math.min(minTop, svgTop);
        maxRight = Math.max(maxRight, right);
        maxBottom = Math.max(maxBottom, bottom);
      } catch (e) {
        // Se getBBox falhar, usar método alternativo
        const rect = el.getBoundingClientRect();
        const containerRect = element.getBoundingClientRect();
        const relativeLeft = rect.left - containerRect.left;
        const relativeTop = rect.top - containerRect.top;
        const relativeRight = relativeLeft + rect.width;
        const relativeBottom = relativeTop + rect.height;
        
        minLeft = Math.min(minLeft, relativeLeft);
        minTop = Math.min(minTop, relativeTop);
        maxRight = Math.max(maxRight, relativeRight);
        maxBottom = Math.max(maxBottom, relativeBottom);
      }
    } else if (position === 'absolute' || position === 'fixed') {
      // Para elementos posicionados absolutamente, usar getBoundingClientRect para obter dimensões reais
      const rect = el.getBoundingClientRect();
      const containerRect = element.getBoundingClientRect();
      
      const relativeLeft = rect.left - containerRect.left;
      const relativeTop = rect.top - containerRect.top;
      const relativeRight = relativeLeft + rect.width;
      const relativeBottom = relativeTop + rect.height;
      
      // Para cards, considerar também scrollHeight para capturar conteúdo que pode estar cortado
      if (el.classList.toString().includes('absolute') && (el.classList.toString().includes('flex-col') || el.offsetHeight > 0)) {
        const scrollHeight = el.scrollHeight || el.offsetHeight;
        const scrollWidth = el.scrollWidth || el.offsetWidth;
        
        // Usar a maior dimensão entre offset e scroll para garantir que conteúdo expandido seja incluído
        const actualHeight = Math.max(rect.height, scrollHeight);
        const actualWidth = Math.max(rect.width, scrollWidth);
        
        minLeft = Math.min(minLeft, relativeLeft);
        minTop = Math.min(minTop, relativeTop);
        maxRight = Math.max(maxRight, relativeLeft + actualWidth);
        maxBottom = Math.max(maxBottom, relativeTop + actualHeight);
      } else {
        minLeft = Math.min(minLeft, relativeLeft);
        minTop = Math.min(minTop, relativeTop);
        maxRight = Math.max(maxRight, relativeRight);
        maxBottom = Math.max(maxBottom, relativeBottom);
      }
    } else {
      // Para elementos normais, usar getBoundingClientRect
      const rect = el.getBoundingClientRect();
      const containerRect = element.getBoundingClientRect();

      const relativeLeft = rect.left - containerRect.left;
      const relativeTop = rect.top - containerRect.top;
      const relativeRight = relativeLeft + rect.width;
      const relativeBottom = relativeTop + rect.height;

      minLeft = Math.min(minLeft, relativeLeft);
      minTop = Math.min(minTop, relativeTop);
      maxRight = Math.max(maxRight, relativeRight);
      maxBottom = Math.max(maxBottom, relativeBottom);
    }
  });

  // Processar paths SVG dentro do SVG para garantir que as setas sejam incluídas
  const svgElements = element.querySelectorAll('svg');
  svgElements.forEach((svg) => {
    const paths = svg.querySelectorAll('path');
    paths.forEach((path) => {
      try {
        const pathElement = path as SVGPathElement;
        const bbox = pathElement.getBBox();
        const svgRect = svg.getBoundingClientRect();
        const containerRect = element.getBoundingClientRect();
        
        const svgLeft = svgRect.left - containerRect.left;
        const svgTop = svgRect.top - containerRect.top;
        
        const pathLeft = svgLeft + bbox.x;
        const pathTop = svgTop + bbox.y;
        const pathRight = pathLeft + bbox.width;
        const pathBottom = pathTop + bbox.height;
        
        minLeft = Math.min(minLeft, pathLeft);
        minTop = Math.min(minTop, pathTop);
        maxRight = Math.max(maxRight, pathRight);
        maxBottom = Math.max(maxBottom, pathBottom);
      } catch (e) {
        // Ignorar erros de getBBox
      }
    });
  });

  // Calcular bounding box real do conteúdo (sem áreas em branco desnecessárias)
  // Usar apenas o que realmente tem conteúdo, com um pequeno padding
  const padding = 50; // Padding reduzido para evitar áreas em branco
  
  console.log('[getFullContentDimensions] Bounds encontrados:', { minLeft, minTop, maxRight, maxBottom });
  
  // Se encontramos elementos, usar suas dimensões reais
  // Caso contrário, usar dimensões padrão do canvas
  let contentWidth: number;
  let contentHeight: number;
  
  if (minLeft !== Infinity && minTop !== Infinity && maxRight > 0 && maxBottom > 0) {
    // Normalizar para começar do zero (remover offset negativo se houver)
    const normalizedMinLeft = Math.min(0, minLeft);
    const normalizedMinTop = Math.min(0, minTop);
    
    // Calcular dimensões baseadas no conteúdo real, começando do zero
    contentWidth = maxRight - normalizedMinLeft + padding;
    contentHeight = maxBottom - normalizedMinTop + padding;
    
    // Garantir tamanho mínimo razoável
    contentWidth = Math.max(contentWidth, 800);
    contentHeight = Math.max(contentHeight, 600);
    
    console.log('[getFullContentDimensions] Dimensões calculadas do conteúdo:', { contentWidth, contentHeight });
  } else {
    // Fallback: usar dimensões do elemento
    contentWidth = Math.max(element.scrollWidth, element.offsetWidth, 800);
    contentHeight = Math.max(element.scrollHeight, element.offsetHeight, 600);
    
    console.log('[getFullContentDimensions] Usando fallback (sem elementos encontrados):', { contentWidth, contentHeight });
  }

  // Restaurar estilos
  element.style.overflow = originalOverflow;
  element.style.position = originalPosition;
  element.style.transform = originalTransform || tempTransform;

  console.log('[getFullContentDimensions] Dimensões finais:', { width: Math.ceil(contentWidth), height: Math.ceil(contentHeight) });
  
  return {
    width: Math.ceil(contentWidth),
    height: Math.ceil(contentHeight) 
  };
};

/**
 * Prepara o documento clonado para exportação (usado no callback onclone do html2canvas)
 */
export const prepareClonedDocumentForExport = (clonedDoc: Document, containerId: string = 'canvas-content'): void => {
  const clonedElement = clonedDoc.querySelector(`#${containerId}`) as HTMLElement;
  if (!clonedElement) return;
  
  // Injetar estilo CSS global para garantir textos completos - EXCETO losangos
  const style = clonedDoc.createElement('style');
  style.textContent = `
    /* Proteger losangos - não aplicar estilos que afetem transform */
    *:not([class*="rotate-45"]):not([class*="rotate"]):not([class*="-rotate-45"]) {
      overflow: visible !important;
      overflow-x: visible !important;
      overflow-y: visible !important;
      white-space: normal !important;
      text-overflow: clip !important;
      max-height: none !important;
      max-width: none !important;
      -webkit-line-clamp: none !important;
      line-clamp: none !important;
      word-wrap: break-word !important;
      word-break: break-word !important;
    }
    .truncate:not([class*="rotate-45"]):not([class*="rotate"]):not([class*="-rotate-45"]), 
    [class*="line-clamp"]:not([class*="rotate-45"]):not([class*="rotate"]):not([class*="-rotate-45"]), 
    [class*="overflow-hidden"]:not([class*="rotate-45"]):not([class*="rotate"]):not([class*="-rotate-45"]) {
      overflow: visible !important;
      white-space: normal !important;
      text-overflow: clip !important;
      -webkit-line-clamp: none !important;
      line-clamp: none !important;
      max-height: none !important;
    }
    /* Proteger losangos - manter transformações EXPLÍCITAS */
    [class*="rotate-45"] {
      transform: rotate(45deg) !important;
      width: 160px !important;
      height: 160px !important;
    }
    /* Conteúdo interno do losango */
    [class*="-rotate-45"] {
      transform: rotate(-45deg) !important;
    }
  `;
  clonedDoc.head.appendChild(style);
  
  // Encontrar todos os elementos no documento clonado
  const allElements = clonedElement.querySelectorAll('*');
  
  allElements.forEach((el) => {
    const element = el as HTMLElement;
    
    // PROTEGER LOSANGOS - Não processar elementos com rotate-45, data-card-type="decisao" ou elementos dentro de losangos
    const isDiamond = element.classList.toString().includes('rotate-45') || 
                     element.classList.toString().includes('rotate') ||
                     element.getAttribute('data-card-type') === 'decisao' ||
                     element.getAttribute('data-decision-content') === 'true';
    
    // Verificar se o elemento está dentro de um container de decisão/losango
    let parent = element.parentElement;
    let isInsideDiamond = false;
    while (parent && parent !== clonedElement) {
      if (parent.classList && (parent.classList.toString().includes('rotate-45') || 
          parent.classList.toString().includes('rotate') ||
          parent.getAttribute('data-card-type') === 'decisao' ||
          parent.querySelector('[class*="rotate-45"]'))) {
        isInsideDiamond = true;
        break;
      }
      parent = parent.parentElement;
    }
    
    if (isDiamond || isInsideDiamond) {
      return; // Pular losangos e seus filhos completamente - manter suas transformações intactas
    }
    
    // Remover classes de truncamento
    const classesToRemove = [
      'truncate', 
      'line-clamp-1', 
      'line-clamp-2', 
      'line-clamp-3', 
      'line-clamp-4',
      'overflow-hidden',
      'overflow-x-hidden',
      'overflow-y-hidden'
    ];
    
    classesToRemove.forEach((cls) => {
      if (element.classList.contains(cls)) {
        element.classList.remove(cls);
      }
    });
    
    // Forçar estilos para permitir texto completo
    element.style.setProperty('overflow', 'visible', 'important');
    element.style.setProperty('overflow-x', 'visible', 'important');
    element.style.setProperty('overflow-y', 'visible', 'important');
    element.style.setProperty('white-space', 'normal', 'important');
    element.style.setProperty('text-overflow', 'clip', 'important');
    element.style.setProperty('max-height', 'none', 'important');
    element.style.setProperty('max-width', 'none', 'important');
    element.style.setProperty('-webkit-line-clamp', 'none', 'important');
    element.style.setProperty('line-clamp', 'none', 'important');
    element.style.setProperty('word-wrap', 'break-word', 'important');
    element.style.setProperty('word-break', 'break-word', 'important');
    
      // Para containers com altura fixa, permitir expansão (mas não losangos)
      const computedStyle = clonedDoc.defaultView?.getComputedStyle(element);
      if (computedStyle && !isDiamond && !isInsideDiamond) {
        // NÃO alterar altura de cards no modo postit - manter altura fixa
        const isPostItCard = element.classList.toString().includes('postit') || 
                            element.closest('[class*="postit"]') !== null;
        
        if (!isPostItCard) {
          // Remover altura fixa e permitir expansão apenas para cards que não são postit
          if ((element.classList.toString().includes('flex-col') ||
               element.classList.toString().includes('h-full') ||
               element.classList.toString().includes('h-') ||
               element.classList.toString().includes('min-h-')) &&
              computedStyle.height !== 'auto') {
            element.style.setProperty('height', 'auto', 'important');
            element.style.setProperty('min-height', computedStyle.height || 'auto', 'important');
            element.style.setProperty('max-height', 'none', 'important');
          }
        } else {
          // Para cards postit, manter altura fixa de 160px
          element.style.setProperty('height', '160px', 'important');
          element.style.setProperty('min-height', '160px', 'important');
          element.style.setProperty('max-height', '160px', 'important');
        }
        
        // Garantir que overflow não corte conteúdo (exceto postit que precisa manter)
        if (!isPostItCard) {
          if (computedStyle.overflow === 'hidden' || 
              computedStyle.overflowY === 'hidden' ||
              element.classList.toString().includes('overflow-hidden')) {
            element.style.setProperty('overflow', 'visible', 'important');
            element.style.setProperty('overflow-y', 'visible', 'important');
            element.style.setProperty('overflow-x', 'visible', 'important');
          }
        }

        // Remover width fixo se necessário para permitir expansão de texto     
        if (element.classList.toString().includes('w-full') ||
            computedStyle.width && computedStyle.width !== 'auto') {
          // Manter width mas permitir expansão vertical
          element.style.setProperty('min-width', computedStyle.width || 'auto', 'important');                                                                     
        }
        
        // Remover transições e animações durante exportação
        element.style.setProperty('transition', 'none', 'important');
        element.style.setProperty('animation', 'none', 'important');
      }
      
      // Proteger losangos - garantir que transform seja mantido
      if (isDiamond || isInsideDiamond) {
        const diamondElement = isDiamond ? element : element.closest('[class*="rotate-45"]');
        if (diamondElement) {
          const diamondComputed = clonedDoc.defaultView?.getComputedStyle(diamondElement as HTMLElement);
          if (diamondComputed) {
            // Manter transform rotate-45
            (diamondElement as HTMLElement).style.setProperty('transform', 'rotate(45deg)', 'important');
            (diamondElement as HTMLElement).style.setProperty('width', '160px', 'important');
            (diamondElement as HTMLElement).style.setProperty('height', '160px', 'important');
            (diamondElement as HTMLElement).style.setProperty('transition', 'none', 'important');
            (diamondElement as HTMLElement).style.setProperty('animation', 'none', 'important');
          }
        }
      }
  });
  
  // Ajustar container principal
  clonedElement.style.setProperty('overflow', 'visible', 'important');
  clonedElement.style.setProperty('position', 'relative', 'important');
};

